// Connection states
export enum ConnectionStatus {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
}

// The AI panel's loading marker: a resume is outstanding, or a history page is being read.
export type AiChatLoading = "resuming" | "loading-history" | null;

// Agent SDK event types
export enum AiEventType {
  USER_MESSAGE = "user.message",
  AGENT_MESSAGE = "agent.message",
  TOOL_USE = "agent.tool_use",
  TOOL_RESULT = "agent.tool_result",
  STATUS_RUNNING = "session.status_running",
  STATUS_IDLE = "session.status_idle",
  STATUS_TERMINATED = "session.status_terminated",
  STATUS_PROGRESS = "session.status_progress",
  STATUS_PROVISIONING = "session.provisioning",
  SESSION_ERROR = "session.error",
  CONNECTION_ERROR = "connection.error",
  APPROVAL_REQUEST = "approval_request",
  QUESTION = "agent.question",
  BACKGROUND_TASKS = "session.background_tasks",
}

export interface UserMessageEvent {
  type: AiEventType.USER_MESSAGE;
  // Set on a replayed user message (the opencode part id, omega-ai-agent-service src/replay.ts);
  // absent on the bubble the panel adds itself when the user sends.
  id?: string;
  content: string;
  pageId?: string;
  timestamp: number;
}

export interface AgentMessageEvent {
  type: AiEventType.AGENT_MESSAGE;
  id?: string;
  content: string;
  timestamp: number;
}

export interface ToolUseEvent {
  type: AiEventType.TOOL_USE;
  id: string;
  name: string;
  input: Record<string, unknown>;
  timestamp: number;
}

export interface ToolResultEvent {
  type: AiEventType.TOOL_RESULT;
  toolUseId: string;
  output: string;
  timestamp: number;
}

export interface StatusEvent {
  type:
    | AiEventType.STATUS_RUNNING
    | AiEventType.STATUS_IDLE
    | AiEventType.STATUS_TERMINATED;
  timestamp: number;
}

export interface StatusProgressEvent {
  type: AiEventType.STATUS_PROGRESS;
  stage: string;
  text: string;
  timestamp: number;
}

// The workspace (the Account's pod) is starting and the model has not been prompted yet. Added by
// the panel the moment a new chat's first message is sent or a past chat is opened, and on the
// agent-service's session.provisioning acknowledgement (agent-chat-speed Decision 5;
// omega-ai-agent-service pages/agent-service/websocket-protocol.html).
export interface StatusProvisioningEvent {
  type: AiEventType.STATUS_PROVISIONING;
  timestamp: number;
}

export interface SessionErrorEvent {
  type: AiEventType.SESSION_ERROR;
  error: string;
  code?: string;
  timestamp: number;
}

export interface ConnectionErrorEvent {
  type: AiEventType.CONNECTION_ERROR;
  error: string;
  retryable: boolean;
  timestamp: number;
}

export interface ApprovalRequestEvent {
  type: AiEventType.APPROVAL_REQUEST;
  id: string;
  toolName: string;
  input: Record<string, unknown>;
  timestamp: number;
}

export interface QuestionOption {
  label: string;
  description?: string;
}

export interface QuestionSpec {
  header: string;
  question: string;
  options: QuestionOption[];
  multiple: boolean;
  custom: boolean;
}

export interface QuestionEvent {
  type: AiEventType.QUESTION;
  requestID: string;
  questions: QuestionSpec[];
  answered?: boolean;
  timestamp: number;
}

// The Background Tasks the agent has running (Claude Code's run_in_background, the Background button). The agent service sends the whole list each
// time: an empty list means none is left (omega-ai-agent-service pages/agent-service/websocket-protocol.html, session.background_tasks).
export interface BackgroundTask {
  id: string;
  description: string;
}
export interface BackgroundTasksEvent {
  type: AiEventType.BACKGROUND_TASKS;
  tasks: BackgroundTask[];
  timestamp: number;
}

export type AiEvent =
  | UserMessageEvent
  | AgentMessageEvent
  | ToolUseEvent
  | ToolResultEvent
  | StatusEvent
  | StatusProgressEvent
  | StatusProvisioningEvent
  | SessionErrorEvent
  | ConnectionErrorEvent
  | ApprovalRequestEvent
  | QuestionEvent
  | BackgroundTasksEvent;
